"use client";

import { useEffect } from "react";
import posthogSingleton from "posthog-js";
import { usePostHog } from "posthog-js/react";
import { readAnalyticsConsent } from "@/components/analytics/browser-attribution";
import { subscribeToBrowserConsentCategory } from "@/components/analytics/browser-consent-lifecycle";
import { POSTHOG_BROWSER_INITIALIZED_EVENT } from "@/lib/analytics/posthog-browser-lifecycle";
import { GA_ID } from "@/lib/analytics/google-ids";
import {
  sendGoogleTagEventIfReady,
  subscribeToGoogleTagDestinationReady,
} from "@/lib/analytics/google-tag-readiness";
import {
  ANALYTICS_LOCATION_CHANGE_EVENT,
  isSuppressedUrlString,
} from "@/lib/analytics/suppressed-paths";

/**
 * On a suppressed (token-bearing) URL — e.g. /signup?redirect_url=/team/join/…
 * — GA is disabled by the navigation barrier and PostHog's before_send drops
 * the event, so a capture would be silently lost. The fire-once keys must not
 * be consumed then; the location-change listener retries after navigation.
 */
function isCurrentUrlSuppressed(): boolean {
  return isSuppressedUrlString(
    `${window.location.pathname}${window.location.search}`,
  );
}

/**
 * Fires the signup-start events when the signup page loads.
 *
 * Naming (issue #1385, human decision 2026-07-19): the PostHog event is
 * `signup_started` — the activation contract's name. The GA4/gtag event
 * stays `sign_up_start`: Google Ads conversion mappings key on it and
 * renaming it would silently break conversion delivery. (PR #1393 briefly
 * standardised the contract the other way; the maintainer's decision is
 * signup_started, with the old sign_up_start PostHog history left queryable.)
 * Uses sessionStorage to ensure each destination only fires once per session
 * (prevents duplicate events from page refreshes). PostHog starts are retried
 * if the visitor accepts analytics cookies after the signup page has loaded.
 */
export function SignUpStartTracker({
  source = "signup_page",
}: {
  /** Which signup surface fired the start — "signup_page" (default) or the
   *  free-first-job funnel's "start_project_save" (Codex P2 on #1396: the
   *  draft path renders its own account form and never mounted this). */
  source?: string;
} = {}) {
  const posthogClient = usePostHog();

  useEffect(() => {
    const legacyKey = "tsb_signup_start_fired";
    const gaKey = "tsb_signup_start_ga_fired";
    const posthogKey = "tsb_signup_start_posthog_fired";
    const payload = { source };

    function hasAcceptedAnalyticsConsent() {
      return readAnalyticsConsent() === "accepted";
    }

    function captureGaSignupStart() {
      if (
        !hasAcceptedAnalyticsConsent() ||
        isCurrentUrlSuppressed() ||
        sessionStorage.getItem(legacyKey) ||
        sessionStorage.getItem(gaKey)
      ) {
        return;
      }

      if (
        sendGoogleTagEventIfReady("analytics", "sign_up_start", {
          value: 1,
          currency: "GBP",
          send_to: GA_ID,
        })
      ) {
        sessionStorage.setItem(gaKey, "1");
        if (sessionStorage.getItem(posthogKey)) {
          sessionStorage.setItem(legacyKey, "1");
        }
      }
    }

    function capturePostHogSignupStart() {
      if (
        sessionStorage.getItem(legacyKey) ||
        sessionStorage.getItem(posthogKey) ||
        !hasAcceptedAnalyticsConsent() ||
        isCurrentUrlSuppressed() ||
        !posthogSingleton.__loaded ||
        !posthogClient ||
        posthogSingleton.has_opted_out_capturing?.()
      ) {
        return;
      }

      posthogClient.capture("signup_started", payload);
      // Only mark delivery after the consent-gated singleton is ready and the
      // capture call has reached it. An uninitialised SDK must never consume
      // the retry opportunity.
      sessionStorage.setItem(posthogKey, "1");
      if (sessionStorage.getItem(gaKey)) {
        sessionStorage.setItem(legacyKey, "1");
      }
    }

    function captureSignupStart() {
      // Keep GA first: the PostHog path writes the legacy all-destinations
      // dedupe key for sessions created by older builds.
      captureGaSignupStart();
      capturePostHogSignupStart();
    }

    captureSignupStart();

    function onConsentChange() {
      // Defer to a microtask so PostHogProvider's own cookie_consent_change
      // listener (which calls posthog.opt_in_capturing()) runs first. Both
      // listeners fire synchronously on the same event, and on a fresh page
      // load this tracker's listener registers BEFORE the ancestor provider's
      // (child effects run before parent effects). Capturing synchronously
      // here would hit posthog.capture() while still opted out — a silent
      // no-op — and then set the dedupe keys, permanently dropping the event.
      // Never trust event detail or the localStorage mirror as consent: the
      // capture helpers re-read the authoritative cookie in this microtask.
      queueMicrotask(captureSignupStart);
    }

    const unsubscribeConsent = subscribeToBrowserConsentCategory(
      "analytics",
      onConsentChange,
    );
    const unsubscribeGoogleReady = subscribeToGoogleTagDestinationReady(
      "analytics",
      captureGaSignupStart,
    );
    window.addEventListener(
      POSTHOG_BROWSER_INITIALIZED_EVENT,
      captureSignupStart,
    );
    // Retry after SPA navigation: a suppressed signup URL skipped WITHOUT
    // consuming the fire-once keys, so the next safe URL must re-attempt.
    window.addEventListener(ANALYTICS_LOCATION_CHANGE_EVENT, captureSignupStart);
    return () => {
      unsubscribeConsent();
      unsubscribeGoogleReady();
      window.removeEventListener(
        POSTHOG_BROWSER_INITIALIZED_EVENT,
        captureSignupStart,
      );
      window.removeEventListener(
        ANALYTICS_LOCATION_CHANGE_EVENT,
        captureSignupStart,
      );
    };
  }, [posthogClient, source]);

  return null;
}
